import React from 'react';

import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import { Box, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import { useDeleteAppExclusiveSchedule } from '~/hooks/apis/offers/useDeleteAppExclusiveSchedule';

import type { ScheduleRecord } from '~/services/OffersService';

import {
  AppExclusiveStatus,
  formatLengthLabel,
  formatSlotTime,
  formatSlotTimeOfDay,
  getOfferLocationLabel,
  getScheduleLengthDays,
  getScheduleStatus,
} from './appOnlyFlashCalendar';

const STATUS_LABELS: Record<AppExclusiveStatus, string> = {
  scheduled: 'Scheduled',
  live: 'Live',
  ended: 'Ended',
};

interface Props {
  schedule: ScheduleRecord;
  offer: App.Offer | undefined;
  isOfferLoading: boolean;
  onMove: (schedule: ScheduleRecord) => void;
}

export default function AppOnlyFlashEventCard({ schedule, offer, isOfferLoading, onMove }: Props) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const showConfirmDialog = useConfirmDialog();
  const deleteSchedule = useDeleteAppExclusiveSchedule();

  const status = getScheduleStatus(schedule);
  const accent = {
    scheduled: theme.palette.info.main,
    live: theme.palette.success.main,
    ended: theme.palette.grey[500],
  }[status];
  const offerName = offer?.name ?? schedule.fk_offer_salesforce_id;
  const locationLabel = getOfferLocationLabel(offer?.location);
  const slotRange = `${formatSlotTime(schedule.start)} – ${formatSlotTime(schedule.end)}`;
  const lengthDays = getScheduleLengthDays(schedule);
  const isMultiDay = lengthDays > 1;
  // Ended rows are history; removing one would let the offer run as App Only Flash again.
  const canRemove = status !== 'ended';
  // A live deal's day has started, so only upcoming ones can change day.
  const canMove = status === 'scheduled';

  const handleRemove = async () => {
    const confirmed = await showConfirmDialog({
      title: 'Remove App Only Flash deal?',
      description: `${offerName} will be removed from ${slotRange} (Sydney). Every day it covers becomes free.`,
    });
    if (!confirmed) {
      return;
    }
    try {
      await deleteSchedule.mutateAsync({ offerId: schedule.fk_offer_salesforce_id, scheduleId: schedule.id });
      enqueueSnackbar(`${offerName} removed from App Only Flash`, { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(`Could not remove deal: ${err?.message ?? 'Unknown error'}`, { variant: 'error' });
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        mb: 0.5,
        borderRadius: 0.75,
        border: 1,
        borderColor: alpha(accent, 0.25),
        borderLeft: 3,
        borderLeftColor: accent,
        bgcolor: alpha(accent, status === 'ended' ? 0.06 : 0.1),
        '&:hover': { boxShadow: 1 },
      }}
    >
      <Tooltip
        title={`${offerName} · ${formatLengthLabel(lengthDays)} · ${slotRange} (Sydney) · ${STATUS_LABELS[status]}`}
      >
        <Box
          component={RouterLink}
          to={`/offers/${schedule.fk_offer_salesforce_id}`}
          aria-label={`${offerName}, ${STATUS_LABELS[status]}`}
          sx={{
            display: 'block',
            px: 0.75,
            py: 0.75,
            pr: canMove ? 6.5 : canRemove ? 3.5 : 0.75,
            color: 'text.primary',
            textDecoration: 'none',
          }}
        >
          {isOfferLoading && !offer ? (
            <Skeleton width="80%" />
          ) : (
            <Typography variant="caption" component="p" fontWeight={500} noWrap>
              {offerName}
            </Typography>
          )}
          {locationLabel && (
            <Typography variant="caption" component="p" color="text.secondary" noWrap sx={{ fontSize: 10 }}>
              {locationLabel}
            </Typography>
          )}
          <Stack direction="row" justifyContent="space-between" mt={0.5}>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: 10 }}>
              {isMultiDay ? `${formatLengthLabel(lengthDays)} · ${slotRange}` : formatSlotTimeOfDay(schedule.start)}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: 10, color: accent }}>
              {STATUS_LABELS[status]}
            </Typography>
          </Stack>
        </Box>
      </Tooltip>
      {canRemove && (
        <Stack direction="row" sx={{ position: 'absolute', top: 2, right: 2 }}>
          {canMove && (
            <Tooltip title="Move or swap deal">
              <IconButton size="small" aria-label={`Move ${offerName}`} onClick={() => onMove(schedule)}>
                <EventRepeatIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Remove deal">
            <IconButton
              size="small"
              aria-label={`Remove ${offerName}`}
              onClick={handleRemove}
              disabled={deleteSchedule.isPending}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
    </Box>
  );
}
